import React, { useState } from 'react';
import { X, Share2, Loader2, Mountain, Clock, Navigation } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SavedTrack, AppUser, RouteDifficulty } from '../../types';
import { convertTrackToRoute, formatDistance, formatDuration } from '../../services/trackService';
import { updateGpxRoute } from '../../services/firebaseService';
import { APP_URL } from '../../constants';
import { useToast } from '../../contexts/ToastContext';

interface TrackShareDialogProps {
  track: SavedTrack;
  user: AppUser;
  onClose: () => void;
  onShared: (routeId: string) => void;
}

const DIFFICULTIES: { value: RouteDifficulty; label: string; color: string }[] = [
  { value: 'Easy', label: 'Enkel', color: '#22c55e' },
  { value: 'Medium', label: 'Middels', color: '#3b82f6' },
  { value: 'Hard', label: 'Krevende', color: '#ef4444' },
  { value: 'Extreme', label: 'Ekstrem', color: '#7f1d1d' },
];

const TrackShareDialog: React.FC<TrackShareDialogProps> = ({ track, user, onClose, onShared }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState(track.name);
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState<RouteDifficulty>('Medium');
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleShare = async () => {
    setSharing(true);
    setError(null);
    try {
      let routeId = track.linkedRouteId;

      // Convert track to route if not already done
      if (!routeId) {
        routeId = await convertTrackToRoute(track, user);
        if (!routeId) {
          setError(t('tracks.share_track_error'));
          setSharing(false);
          return;
        }
      }

      // Update route with user-provided metadata and set status to 'shared'
      await updateGpxRoute(routeId, {
        name: name.trim() || track.name,
        description: description.trim(),
        difficulty,
        status: 'shared',
      });

      // Share the URL
      const shareUrl = `${APP_URL}/share/route/${routeId}`;
      if (navigator.share) {
        await navigator.share({
          title: name.trim() || track.name,
          text: t('tracks.share_track_sharing_text', { name: name.trim() || track.name }),
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast(t('tracks.share_track_success'), 'success');
      }

      onShared(routeId);
    } catch (err: any) {
      // User cancelled share dialog — not an error
      if (err?.name === 'AbortError') {
        onShared(track.linkedRouteId || '');
        return;
      }
      setError(t('tracks.share_track_error'));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-end md:items-center justify-center pointer-events-none">
      {/* Backdrop */}
      <div className="pointer-events-auto fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="pointer-events-auto w-full max-w-sm mx-2 bg-white rounded-t-3xl md:rounded-3xl md:mb-4 shadow-2xl border border-stone-200 animate-in slide-in-from-bottom-2 max-h-[85dvh] overflow-y-auto relative" style={{ overscrollBehavior: 'contain', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <div className="flex items-center gap-2">
            <Share2 size={16} className="text-blue-500" />
            <h3 className="text-[13px] font-black text-stone-800">{t('tracks.share_track_title')}</h3>
          </div>
          <button onClick={onClose} className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-muted)]">
            <X size={16} />
          </button>
        </div>

        {/* Track stats */}
        <div className="px-5 pb-3 flex items-center gap-3">
          <span className="flex items-center gap-1 text-2xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg">
            <Navigation size={9} /> {formatDistance(track.distance)}
          </span>
          <span className="flex items-center gap-1 text-2xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg">
            <Clock size={9} /> {formatDuration(track.duration)}
          </span>
          {track.elevationGain > 0 && (
            <span className="flex items-center gap-1 text-2xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-lg">
              <Mountain size={9} /> +{track.elevationGain}m
            </span>
          )}
        </div>

        <div className="px-5 pb-5 space-y-3">
          {/* Name */}
          <div>
            <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1.5">{t('tracks.route_name')}</p>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={track.name}
              className="w-full px-3 py-2.5 bg-stone-50 rounded-xl text-[12px] text-stone-800 placeholder-[var(--text-secondary)] border border-stone-200 outline-none focus:border-emerald-400"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1.5">{t('tracks.route_description')}</p>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('tracks.share_track_description_placeholder')}
              rows={3}
              className="w-full px-3 py-2.5 bg-stone-50 rounded-xl text-[12px] text-stone-800 placeholder-[var(--text-secondary)] border border-stone-200 outline-none focus:border-emerald-400 resize-none"
            />
          </div>

          {/* Difficulty */}
          <div>
            <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">{t('tracks.share_track_difficulty')}</p>
            <div className="flex gap-2">
              {DIFFICULTIES.map(d => (
                <button
                  key={d.value}
                  onClick={() => setDifficulty(d.value)}
                  className={`flex-1 py-2 rounded-xl text-2xs font-bold border transition-colors ${
                    difficulty === d.value
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-stone-200 text-[var(--text-muted)] hover:border-stone-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    {d.label}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-tiny text-red-600">
              {error}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onClose}
              disabled={sharing}
              className="flex-1 py-2.5 bg-stone-100 text-[var(--text-muted)] rounded-xl text-[12px] font-bold hover:bg-stone-200 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleShare}
              disabled={sharing}
              className="flex-1 py-2.5 bg-blue-500 text-white rounded-xl text-[12px] font-bold hover:bg-blue-600 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {sharing ? (
                <>
                  <Loader2 size={13} className="animate-spin" />
                  {t('tracks.share_track_preparing')}
                </>
              ) : (
                <>
                  <Share2 size={13} />
                  {t('tracks.share_track_button')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackShareDialog;
